import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, ChevronRight, X, ZoomIn, ImageIcon,
  Search, LayoutGrid, List, FolderOpen, ArrowLeft,
  Loader, ChevronLeft, ChevronRight as ChevronRightIcon,
  Calendar, Tag,
} from 'lucide-react';
import { fetchGaleri, fetchGaleriAlbum, getImageUrl, formatTanggalIndonesia } from '../utils/api';
import SEO from '../components/SEO';

// ─── Helpers ────────────────────────────────────────────────────────────────

// Fallback: kelompokkan flat foto (struktur lama) menjadi album objects
// cover dinormalisasi ke UUID string agar konsisten dengan struktur baru
const groupByAlbumLegacy = (items) => {
  const map = {};
  items.forEach((item) => {
    const key = item.album || 'Umum';
    if (!map[key]) {
      map[key] = {
        nama: key,
        kategori: item.kategori || 'Lainnya',
        tanggal: item.tanggal,
        fotos: [],
        cover: null,
      };
    }
    map[key].fotos.push(item);
    if (item.is_cover || !map[key].cover) {
      map[key].cover = item.foto; // UUID string
    }
  });
  return Object.values(map).sort((a, b) =>
    new Date(b.tanggal) - new Date(a.tanggal)
  );
};

// ─── Lightbox ───────────────────────────────────────────────────────────────

const Lightbox = ({ fotos, index, onClose }) => {
  const [current, setCurrent] = useState(index);

  const prev = () => setCurrent((c) => (c - 1 + fotos.length) % fotos.length);
  const next = () => setCurrent((c) => (c + 1) % fotos.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const foto = fotos[current];
  const imgUrl = getImageUrl(foto.foto, { width: 1200, quality: 90, format: 'webp' });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black/95 flex flex-col items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 bg-gradient-to-b from-black/60 to-transparent z-10">
        <div>
          <p className="text-white font-semibold text-sm">{foto.judul}</p>
          <p className="text-white/50 text-xs">{current + 1} / {fotos.length}</p>
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white transition-colors p-2">
          <X size={24} />
        </button>
      </div>

      {/* Image */}
      <motion.div
        key={current}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="relative max-w-5xl w-full max-h-[80vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imgUrl}
          alt={foto.judul}
          className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
        />
      </motion.div>

      {/* Navigasi */}
      {fotos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 transition-all"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 transition-all"
          >
            <ChevronRightIcon size={24} />
          </button>
        </>
      )}

      {/* Caption */}
      {foto.keterangan && (
        <p className="absolute bottom-6 text-white/60 text-sm text-center px-8 max-w-xl">
          {foto.keterangan}
        </p>
      )}
    </motion.div>
  );
};

// ─── Album Grid Card ─────────────────────────────────────────────────────────

const AlbumCard = ({ album, onClick }) => {
  // cover bisa UUID string (struktur baru) atau object {foto: uuid} (legacy)
  const coverFileId = typeof album.cover === 'string' ? album.cover : album.cover?.foto;
  const coverUrl = getImageUrl(coverFileId, { width: 600, height: 400, quality: 80, format: 'webp' });
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="group cursor-pointer bg-white dark:bg-[#1A182E] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-800"
      onClick={onClick}
    >
      <div className="relative overflow-hidden aspect-video">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={album.nama}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#7B2FBE]/20 to-[#1A1A2E] flex items-center justify-center">
            <FolderOpen size={40} className="text-[#7B2FBE]/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
        <div className="absolute top-3 right-3 bg-black/50 text-white text-xs font-medium px-2.5 py-1 rounded-full backdrop-blur-sm flex items-center gap-1">
          <ImageIcon size={11} /> {album.fotos.length} foto
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 dark:text-white text-sm leading-snug mb-2 group-hover:text-[#7B2FBE] transition-colors line-clamp-2">
          {album.nama}
        </h3>
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          {album.kategori && (
            <span className="flex items-center gap-1">
              <Tag size={11} className="text-[#7B2FBE]" /> {album.kategori}
            </span>
          )}
          {album.tanggal && (
            <span className="flex items-center gap-1">
              <Calendar size={11} /> {formatTanggalIndonesia(album.tanggal)}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ─── Album List Row ──────────────────────────────────────────────────────────

const AlbumRow = ({ album, onClick }) => {
  const coverFileId = typeof album.cover === 'string' ? album.cover : album.cover?.foto;
  const coverUrl = getImageUrl(coverFileId, { width: 120, height: 80, quality: 75, format: 'webp' });
  return (
    <motion.div
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className="flex items-center gap-4 p-4 bg-white dark:bg-[#1A182E] rounded-xl border border-gray-100 dark:border-gray-800 hover:border-[#7B2FBE]/40 hover:shadow-md transition-all cursor-pointer group"
      onClick={onClick}
    >
      <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-800">
        {coverUrl ? (
          <img src={coverUrl} alt={album.nama} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FolderOpen size={22} className="text-gray-400" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-gray-900 dark:text-white text-sm group-hover:text-[#7B2FBE] transition-colors truncate">{album.nama}</h3>
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
          {album.kategori && <span className="flex items-center gap-1"><Tag size={10} className="text-[#7B2FBE]" />{album.kategori}</span>}
          {album.tanggal && <span className="flex items-center gap-1"><Calendar size={10} />{formatTanggalIndonesia(album.tanggal)}</span>}
        </div>
      </div>
      <div className="flex-shrink-0 text-right">
        <span className="text-xs font-medium text-[#7B2FBE] bg-[#7B2FBE]/10 px-2.5 py-1 rounded-full">
          {album.fotos.length} foto
        </span>
      </div>
    </motion.div>
  );
};

// ─── MAIN GALERI ─────────────────────────────────────────────────────────────

const Galeri = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [search, setSearch] = useState('');
  const [activeKategori, setActiveKategori] = useState('Semua');
  const [openAlbum, setOpenAlbum] = useState(null); // album object
  const [lightbox, setLightbox] = useState(null);   // { fotos, index }

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // Coba struktur baru (galeri_album relasional) dulu
      const albumData = await fetchGaleriAlbum();
      if (albumData.length > 0) {
        setAlbums(albumData);
      } else {
        // Fallback ke struktur lama (flat foto + grouping)
        const fotos = await fetchGaleri();
        setAlbums(groupByAlbumLegacy(fotos));
      }
      setLoading(false);
    };
    load();
  }, []);

  // Daftar kategori unik dari album
  const kategoriList = useMemo(() => {
    const set = new Set(albums.map((a) => a.kategori).filter(Boolean));
    return ['Semua', ...Array.from(set)];
  }, [albums]);

  // Filter & search album
  const filteredAlbums = useMemo(() => {
    let result = activeKategori === 'Semua'
      ? albums
      : albums.filter((a) => a.kategori === activeKategori);
    if (search.trim()) {
      result = result.filter((a) =>
        a.nama.toLowerCase().includes(search.toLowerCase())
      );
    }
    return result;
  }, [albums, activeKategori, search]);

  // Foto dalam album yang sedang dibuka (langsung dari album.fotos)
  const albumFotos = openAlbum?.fotos || [];

  const totalFoto = albums.reduce((sum, a) => sum + (a.fotos?.length || 0), 0);

  return (
    <div data-testid="galeri-page">
      <SEO
        title="Galeri Foto"
        description="Galeri foto kegiatan akademik, wisuda, kemahasiswaan, dan fasilitas STIKES Harapan Ibu Jambi."
      />

      {/* Hero */}
      <div className="page-hero pt-[70px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1"><Home size={14} /> Beranda</Link>
              <ChevronRight size={14} />
              {openAlbum ? (
                <>
                  <button onClick={() => setOpenAlbum(null)} className="hover:text-white transition-colors">Galeri Foto</button>
                  <ChevronRight size={14} />
                  <span className="text-white">{openAlbum.nama}</span>
                </>
              ) : (
                <span className="text-white">Galeri Foto</span>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white font-heading mb-4">
              {openAlbum ? openAlbum.nama : 'Galeri Foto'}
            </h1>
            {!openAlbum && (
              <p className="text-white/80 max-w-xl text-base">Abadikan momen berharga kegiatan akademik, kemahasiswaan, dan fasilitas STIKES HI Jambi.</p>
            )}
          </motion.div>
        </div>
      </div>

      <div className="bg-[#F8F7FF] dark:bg-[#121020] min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

          {/* ── ALBUM DETAIL VIEW ── */}
          {openAlbum ? (
            <>
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <button
                  onClick={() => setOpenAlbum(null)}
                  className="flex items-center gap-2 text-[#7B2FBE] hover:text-[#6b25a8] font-medium text-sm transition-colors"
                >
                  <ArrowLeft size={16} /> Kembali ke semua album
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <ImageIcon size={14} className="text-[#7B2FBE]" /> {albumFotos.length} foto
                </span>
              </div>

              {loading ? (
                <div className="flex justify-center py-24">
                  <Loader className="animate-spin text-[#7B2FBE]" size={36} />
                </div>
              ) : (
                <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4 pb-16">
                  {albumFotos.map((foto, i) => {
                    const imgUrl = getImageUrl(foto.foto, { width: 600, quality: 80, format: 'webp' });
                    return (
                      <motion.div
                        key={foto.id}
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.35, delay: i * 0.03 }}
                        className="break-inside-avoid group cursor-pointer relative overflow-hidden rounded-xl shadow-sm hover:shadow-xl transition-all duration-300"
                        onClick={() => setLightbox({ fotos: albumFotos, index: i })}
                      >
                        <img
                          src={imgUrl}
                          alt={foto.judul}
                          loading="lazy"
                          className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                          <ZoomIn size={26} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <p className="text-white text-xs font-medium line-clamp-1">{foto.judul}</p>
                          {foto.keterangan && <p className="text-white/60 text-xs line-clamp-1">{foto.keterangan}</p>}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            /* ── ALBUM LIST VIEW ── */
            <>
              {/* Stats */}
              <div className="flex items-center gap-4 mb-6 text-sm text-gray-500 dark:text-gray-400 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <FolderOpen size={14} className="text-[#7B2FBE]" /> {filteredAlbums.length} album
                </span>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span className="flex items-center gap-1.5">
                  <ImageIcon size={14} className="text-[#7B2FBE]" /> {totalFoto} foto
                </span>
              </div>

              {/* Toolbar: Search + Kategori + View Toggle */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                {/* Search */}
                <div className="relative flex-1">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cari nama album..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#7B2FBE] transition-colors"
                  />
                </div>

                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-white dark:bg-[#1A182E] border border-gray-200 dark:border-gray-700 rounded-xl p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all ${
                      viewMode === 'grid'
                        ? 'bg-[#7B2FBE] text-white'
                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <LayoutGrid size={15} /> Grid
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all ${
                      viewMode === 'list'
                        ? 'bg-[#7B2FBE] text-white'
                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <List size={15} /> List
                  </button>
                </div>
              </div>

              {/* Kategori Filter */}
              <div className="flex gap-2 flex-wrap mb-8">
                {kategoriList.map((kat) => (
                  <button
                    key={kat}
                    onClick={() => setActiveKategori(kat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeKategori === kat
                        ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/20'
                        : 'bg-white dark:bg-[#1A182E] text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-[#7B2FBE]/50'
                    }`}
                  >
                    {kat}
                  </button>
                ))}
              </div>

              {/* Loading */}
              {loading ? (
                <div className="flex justify-center py-24">
                  <Loader className="animate-spin text-[#7B2FBE]" size={36} />
                </div>
              ) : filteredAlbums.length === 0 ? (
                <div className="text-center py-24 text-gray-400">
                  <FolderOpen size={48} className="mx-auto mb-4 opacity-30" />
                  <p className="font-medium">Album tidak ditemukan</p>
                  <p className="text-sm mt-1">Coba kata kunci lain</p>
                </div>
              ) : viewMode === 'grid' ? (
                /* Grid View */
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 pb-16">
                  {filteredAlbums.map((album) => (
                    <AlbumCard
                      key={album.id || album.nama}
                      album={album}
                      onClick={() => setOpenAlbum(album)}
                    />
                  ))}
                </div>
              ) : (
                /* List View */
                <div className="flex flex-col gap-3 pb-16">
                  {filteredAlbums.map((album) => (
                    <AlbumRow
                      key={album.id || album.nama}
                      album={album}
                      onClick={() => setOpenAlbum(album)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <Lightbox
            fotos={lightbox.fotos}
            index={lightbox.index}
            onClose={() => setLightbox(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Galeri;
